import { makeStyles } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { useLocation } from 'react-router-dom';
import { isActivePath } from './activePath';

/**
 * The portal's sections, in the top bar (DX-74, regrouped in DX-302).
 *
 * The arrangement the public Developer Portal uses: the sections across the
 * bar, and the navigation column below showing what is inside the one the
 * reader is in. The bar answers "which part of the portal am I in", the column
 * "where am I inside it" — which is why the same names appear in both without
 * that being duplication.
 *
 * `Create` is still deliberately absent: it is an action, and actions live on
 * the right of the bar with search and the account, not among the places you
 * can be. `Home` used to be absent on the same argument, left to the logo;
 * the target picture puts it in the bar, and it is there now.
 *
 * Whether the column should narrow to the current section, as the Developer
 * Portal's does, is the open half of this — see portal-shell.md.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    /*
      The bar is one row and everything in it competes for the same width. The
      sections give way before the logo, the search or the account do — a
      reader who cannot see them still has the whole sidebar, while a bar
      without its account menu has lost the way out.
    */
    minWidth: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  link: {
    display: 'block',
    // The Developer Portal's pills: `rounded-full px-3 py-1.5 text-sm`, in
    // the bar's ink until hovered or current (DX-352).
    padding: '6px 12px',
    borderRadius: 'var(--radius-pill)',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    color: 'var(--ui-bar-ink)',
    textDecoration: 'none',
    transition: 'background 0.15s ease, color 0.15s ease',
    '&:hover': {
      color: '#FFFFFF',
      background: 'rgba(255, 255, 255, 0.10)',
      textDecoration: 'none',
    },
    '.dark &:hover': {
      background: 'rgba(255, 255, 255, 0.15)',
    },
  },
  /*
    The current section is filled rather than underlined: the bar sits directly
    above a light page, and an underline on a dark strip reads as the page's
    own border rather than as a mark on the link.
  */
  linkActive: {
    color: '#FFFFFF',
    background: 'rgba(255, 255, 255, 0.15)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.15)',
    },
  },
}));

/**
 * The five sections the target picture puts in the bar (DX-302).
 *
 * `Home` is here now, where it was previously left to the logo. The target
 * picture marks it as a section like any other, and a bar whose first entry is
 * the one every reader starts from is easier to read than one that expects
 * them to know the logo is a link.
 *
 * `Operate` is a group rather than a page: Status, Deployments, Scorecards and
 * Access sit under it in the column. It leads to Status, the first of them —
 * a section entry has to arrive somewhere, and an overview page over four
 * pages that do not exist yet would be four empty cards.
 *
 * `Areas` and `Responsibility` are gone from the bar. The four fields are not
 * in the target picture at all; the responsibility map is, but as a register
 * in the column rather than a section — it is one page, and the bar names
 * parts of the portal that have pages under them.
 */
const SECTIONS = [
  { to: '/', label: 'Home' },
  { to: '/catalog', label: 'Catalog' },
  { to: '/docs', label: 'Docs' },
  {
    to: '/status',
    label: 'Operate',
    family: ['/deployments', '/scorecards', '/security', '/access'],
  },
  { to: '/ask', label: 'Ask' },
] as const;

/**
 * Whether the reader is inside a section.
 *
 * By prefix, so a service's own page still marks the catalog it came from —
 * the bar answers "where am I", and a reader three levels into an entity has
 * not left. "Operate" is four routes rather than one, because its pages have
 * no shared path of their own: `/status` is where the entry leads and the
 * other three are its family.
 */
const isHere = (section: (typeof SECTIONS)[number], pathname: string) =>
  [section.to, ...(('family' in section && section.family) || [])].some(
    // Home is the one section whose path is a prefix of every other, so it
    // is matched exactly — isActivePath does that for `/`. Without it Home
    // would be marked on every page in the portal, which tells a reader
    // nothing.
    path => isActivePath(pathname, path),
  );

/**
 * The name of the section the reader is in, or `undefined` on a page that
 * belongs to none. The navigation column prints it as its title (DX-352), so
 * the bar and the column cannot disagree about where the reader is.
 */
export const useCurrentSection = () => {
  const { pathname } = useLocation();
  return SECTIONS.find(section => isHere(section, pathname))?.label;
};

export const TopNav = () => {
  const classes = useStyles();
  const { pathname } = useLocation();

  return (
    <nav className={classes.nav} aria-label="Portal sections">
      {SECTIONS.map(section => (
        <Link
          key={section.label}
          to={section.to}
          className={`${classes.link} ${
            isHere(section, pathname) ? classes.linkActive : ''
          }`}
          aria-current={isHere(section, pathname) ? 'page' : undefined}
        >
          {section.label}
        </Link>
      ))}
    </nav>
  );
};
